var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { getStoredAiToken } from './aiTokenStorage';
// ---------------------------------------------------------------------------
// JWT Parsing
// ---------------------------------------------------------------------------
/**
 * Decode JWT payload without verification.
 * Frontend already trusts the token; we just need to read claims.
 */
function decodeJwtPayload(token) {
    try {
        const parts = token.split('.');
        if (parts.length !== 3)
            return null;
        // Base64url decode the payload
        let payload = parts[1];
        // Convert Base64url to Base64
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Restore padding - Base64 requires length to be multiple of 4
        while (payload.length % 4 !== 0) {
            payload += '=';
        }
        const decoded = atob(payload);
        return JSON.parse(decoded);
    }
    catch (_a) {
        return null;
    }
}
/**
 * Extract territory claims from the stored AI JWT.
 * Returns unrestricted access if no token or claims are missing (backward compatibility).
 */
export function getTerritoryInfoFromToken() {
    const token = getStoredAiToken();
    if (!token) {
        // No token = treat as unrestricted (backward compatibility)
        return { hasTerritoriesLimitations: false, permittedTerritoryIds: [] };
    }
    const payload = decodeJwtPayload(token);
    if (!payload) {
        // Fail-secure: if we can't decode the token, assume restrictions apply
        return { hasTerritoriesLimitations: true, permittedTerritoryIds: [] };
    }
    return {
        hasTerritoriesLimitations: payload.has_territories_limitations === true,
        permittedTerritoryIds: Array.isArray(payload.permitted_territory_ids)
            ? payload.permitted_territory_ids.filter((id) => typeof id === 'number')
            : []
    };
}
// ---------------------------------------------------------------------------
// Building Territory Lookup
// ---------------------------------------------------------------------------
/**
 * Fetch building's territory_id from the internal API.
 * Used when page context doesn't include territoryId (URL-parsed context).
 *
 * @returns territory_id or null if fetch failed
 */
export function fetchBuildingTerritory(buildingId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield fetch(`/api/internal/buildings/${buildingId}/territory`, {
                credentials: 'include',
                headers: {
                    Accept: 'application/json'
                }
            });
            if (!response.ok) {
                console.debug('[pageContextValidator] Failed to fetch building territory:', response.status);
                return null;
            }
            const data = (yield response.json());
            return typeof data.territory_id === 'number' ? data.territory_id : null;
        }
        catch (error) {
            console.debug('[pageContextValidator] Error fetching building territory:', error);
            return null;
        }
    });
}
// ---------------------------------------------------------------------------
// Page Context Validation
// ---------------------------------------------------------------------------
/**
 * Validate page context against user's territory restrictions.
 *
 * For unrestricted users: returns context unchanged.
 * For restricted users: validates building's territory is in permitted list.
 * If validation fails or can't be performed: returns undefined (suppresses context).
 *
 * @param context - Page context to validate (may include territoryId from server)
 * @returns Validated context or undefined if suppressed
 */
export function validatePageContext(context) {
    return __awaiter(this, void 0, void 0, function* () {
        // No context = nothing to validate
        if (!context) {
            return undefined;
        }
        const territoryInfo = getTerritoryInfoFromToken();
        // Unrestricted users get context as-is
        if (!territoryInfo.hasTerritoriesLimitations) {
            return context;
        }
        // Dashboard-tier and listing-tier pages have no building scope — pass through
        const NO_BUILDING_PAGE_TYPES = new Set([
            'dashboard',
            'calendar',
            'map',
            'advanced_analytics',
            'buildings_index',
            'inspections_index',
            'work_orders_index',
            'proposals_index',
            'accounts_index',
            'reports_index',
            'inspection_series_index',
            'ai_to_review_inspections',
            // Phase B new listing pages
            'deficiencies_index',
            'contacts_index',
            'invoices_index',
            'account',
            'unknown'
        ]);
        // Resolve the canonical page type and building/territory IDs regardless of v1/v2 shape.
        // v2 payloads carry snake_case fields (page_type, building_id, territory_id);
        // v1 payloads carry camelCase (pageType, buildingId, territoryId).
        const isV2 = context.schema_version === 2;
        const resolvedPageType = isV2 ? context.page_type : context.pageType;
        const resolvedBuildingId = isV2 ? context.building_id : context.buildingId;
        const resolvedTerritoryId = isV2 ? context.territory_id : context.territoryId;
        const hasResolvedBuildingId = resolvedBuildingId !== null && resolvedBuildingId !== undefined;
        // Non-building-scoped page types pass through — unless building_id is also present.
        // A crafted payload could carry building_id alongside a listing/dashboard page_type to
        // smuggle building context past territory validation. If building_id is present we must
        // still validate its territory even on non-building-scoped pages.
        if ((!resolvedPageType || NO_BUILDING_PAGE_TYPES.has(resolvedPageType)) && !hasResolvedBuildingId) {
            return context;
        }
        // No building ID — only entity types that are genuinely not building-scoped pass through.
        // contact and invoice can exist as global resources not tied to a building.
        // deficiency is always building-scoped; suppress if no buildingId is available.
        if (resolvedBuildingId === null || resolvedBuildingId === undefined) {
            if (resolvedPageType === 'contact' || resolvedPageType === 'invoice') {
                return context;
            }
            console.debug('[pageContextValidator] No buildingId, suppressing context');
            return undefined;
        }
        // Get territory ID - from context (server-rendered) or fetch from API
        let resolvedTerritory = resolvedTerritoryId;
        if (resolvedTerritory === undefined) {
            // Context doesn't have territoryId (URL-parsed), fetch from API
            resolvedTerritory = yield fetchBuildingTerritory(resolvedBuildingId);
        }
        // Failed to determine territory = suppress for safety (fail secure)
        if (resolvedTerritory === null) {
            console.debug('[pageContextValidator] Could not determine building territory, suppressing context');
            return undefined;
        }
        // Check if territory is in permitted list
        if (!territoryInfo.permittedTerritoryIds.includes(resolvedTerritory)) {
            console.debug('[pageContextValidator] Building not in permitted territories, suppressing context');
            return undefined;
        }
        // Passed validation - return context with the canonical territoryId populated.
        // For v2 payloads, populate territory_id (snake_case); for v1, populate territoryId.
        if (isV2) {
            return Object.assign(Object.assign({}, context), { territory_id: resolvedTerritory });
        }
        return Object.assign(Object.assign({}, context), { territoryId: resolvedTerritory });
    });
}
